// Data Pipeline flow diagram — geometry + live derivation. Pure functions,
// unit-tested (flow-layout.test.ts). Ported from the approved Claude Design
// mockup (design-bundle/diagram.js §5); the node/edge coordinates ARE the
// approved design — do not hand-tune without a design pass.
//
// The wire types type counts as bigint (ts-rs); JSON delivers plain numbers.
// Everything funnels through `num()` so both are safe (same convention as
// screens/capi-forwarding.tsx).

import type {
  ComponentHealth,
  FlowRateRow,
  PipelineFlowSnapshot,
} from '@/api/types';
import type {
  IngestChannel,
} from '@/api/types.gen';
import { componentDetail, componentLabel, formatStoreDetail } from '@/lib/chip-state';
import { fmtClock } from '@/lib/datetime';
import { ratePct } from '@/lib/flow-masks';
import { bandFor } from '@/lib/system-metrics';

// ── Formatting helpers (mockup DP.* ports) ─────────────────────────────────

export function num(v: bigint | number | null | undefined): number {
  return v == null ? 0 : Number(v);
}

/** Full locale integer, '—' for null (mock DP.n). */
export function dpN(v: number | null | undefined): string {
  return v == null ? '—' : Math.round(v).toLocaleString('en-US');
}

/** Abbreviated count (mock DP.k): 1.2k / 24k / 1.02M. */
export function dpK(v: number): string {
  return v >= 1e6
    ? (v / 1e6).toFixed(2) + 'M'
    : v >= 1e4
      ? Math.round(v / 1e3) + 'k'
      : v >= 1e3
        ? (v / 1e3).toFixed(1) + 'k'
        : String(Math.round(v));
}

export function dpPct(numerator: number, den: number, d = 1): string {
  return den > 0 ? ((numerator / den) * 100).toFixed(d) + '%' : '—';
}

/** A percentage that may be unknown (`null`: nothing to divide by) → "—". */
export function dpRate(pct: number | null | undefined, d = 1): string {
  return pct == null ? '—' : pct.toFixed(d) + '%';
}

/** seconds → "84s" | "1m 24s" | "2h 04m" */
export function dpDur(secs: number | null | undefined): string {
  if (secs == null) return '—';
  if (secs < 60) return Math.round(secs) + 's';
  if (secs < 3600) return Math.floor(secs / 60) + 'm ' + String(Math.round(secs % 60)).padStart(2, '0') + 's';
  return Math.floor(secs / 3600) + 'h ' + String(Math.floor((secs % 3600) / 60)).padStart(2, '0') + 'm';
}

export function dpAgeSecs(iso: string | null | undefined, nowMs: number): number {
  return iso ? Math.max(0, (nowMs - Date.parse(iso)) / 1000) : Infinity;
}

/** age of an RFC3339 timestamp vs now → "4s ago" | "11m ago" | "3h ago" */
export function dpAge(iso: string | null | undefined, nowMs: number): string {
  if (!iso) return 'never';
  const s = Math.max(0, (nowMs - Date.parse(iso)) / 1000);
  if (s < 60) return Math.round(s) + 's ago';
  if (s < 5400) return Math.round(s / 60) + 'm ago';
  if (s < 86400 * 2) return (s / 3600).toFixed(s < 10800 ? 1 : 0) + 'h ago';
  return Math.round(s / 86400) + 'd ago';
}

/** RFC3339 string or epoch-ms → "HH:MM:SS" (America/New_York). */
export function dpClock(x: string | number): string {
  return fmtClock(x);
}

// ── Geometry — 3 lanes × 5 columns, hand-tuned subway-map layout ───────────

export const DP_VB = { w: 1240, h: 568 } as const;
export const DP_CARD = { w: 176, h: 64 } as const;

export const DP_NODE_IDS = [
  'web-pixel', 'sgtm', 'pixel-edge', 'batch-ingest', 'store-web-events',
  'enrich', 'encore-pg', 'ingest-jobs', 'store-tickets', 'd1-stats',
  'conversion-queue', 'meta', 'ttd',
] as const;
export type DpNodeId = (typeof DP_NODE_IDS)[number];

export interface DpNode {
  id: DpNodeId;
  x: number;
  y: number;
  name: string;
  kind: string;
}

// (cols ≈ 40/286/532/778/1024)
export const DP_NODES: readonly DpNode[] = [
  { id: 'web-pixel',           x: 40,   y: 46,  name: 'Web Pixel',        kind: 'SOURCE · SAM.JS BROWSERS' },
  { id: 'sgtm',                x: 40,   y: 134, name: 'Server Pixel',     kind: 'SOURCE · SGTM CONTAINER' },
  { id: 'pixel-edge',          x: 286,  y: 90,  name: 'Pixel Collector',  kind: 'EDGE · PIXEL COLLECTOR' },
  { id: 'batch-ingest',        x: 532,  y: 90,  name: 'Batch Ingest',     kind: 'PROCESS · WEB EVENTS' },
  { id: 'store-web-events',    x: 778,  y: 40,  name: 'Data Warehouse',   kind: 'STORE · WEB EVENTS + DATA LAKE' },
  { id: 'enrich',              x: 778,  y: 152, name: 'Enrich API',       kind: 'PROCESS · PERSON RESOLUTION' },
  { id: 'encore-pg',           x: 40,   y: 300, name: 'EncorePro',        kind: 'SOURCE · ENCOREPRO DATABASE' },
  { id: 'ingest-jobs',         x: 532,  y: 300, name: 'Ingest Jobs',      kind: 'PROCESS · BACKFILL' },
  { id: 'store-tickets',       x: 778,  y: 276, name: 'Data Warehouse',   kind: 'STORE · TICKETS / USERS' },
  { id: 'd1-stats',            x: 778,  y: 368, name: 'Daily Stats',      kind: 'STORE · DATA WAREHOUSE' },
  { id: 'conversion-queue',    x: 778,  y: 460, name: 'Conversion Queue', kind: 'PROCESS · LOCAL QUEUE' },
  { id: 'meta',                x: 1024, y: 424, name: 'Meta CAPI',        kind: 'DESTINATION · GRAPH API' },
  { id: 'ttd',                 x: 1024, y: 496, name: 'The Trade Desk',   kind: 'DESTINATION · OFFLINE CONVERSIONS' },
];

export const DP_EDGE_IDS = ['e1', 'e2', 'e3', 'e4', 'e5', 'e6', 'e7', 'e8', 'e9', 'e10', 'e11', 'e12', 'e13'] as const;
export type DpEdgeId = (typeof DP_EDGE_IDS)[number];

export type DpPoint = readonly [number, number];

export interface DpEdge {
  id: DpEdgeId;
  from: DpNodeId;
  to: DpNodeId;
  /** Orthogonal waypoints (rounded elbows). */
  pts: readonly DpPoint[];
  /** Label anchor in viewBox units. */
  pill: DpPoint;
  /** Number ticks on update (DPTick with reserved width). */
  tick?: boolean;
  /** Bidirectional (arrowheads both ends). */
  bi?: boolean;
  align?: 'left';
  /** Static label (no marching flow overlay). */
  static?: boolean;
}

export const DP_EDGES: readonly DpEdge[] = [
  { id: 'e1',  from: 'web-pixel',        to: 'pixel-edge',          pts: [[216, 78], [251, 78], [251, 107], [286, 107]],              pill: [251, 92],  tick: true },
  { id: 'e2',  from: 'sgtm',             to: 'pixel-edge',          pts: [[216, 166], [251, 166], [251, 137], [286, 137]],            pill: [251, 152], tick: true },
  { id: 'e3',  from: 'pixel-edge',       to: 'batch-ingest',        pts: [[462, 122], [532, 122]],                                    pill: [497, 122], tick: true },
  { id: 'e4',  from: 'batch-ingest',     to: 'store-web-events',    pts: [[708, 104], [743, 104], [743, 72], [778, 72]],              pill: [743, 88],  tick: true },
  { id: 'e5',  from: 'batch-ingest',     to: 'enrich',              pts: [[708, 140], [743, 140], [743, 170], [778, 170]],            pill: [735, 192], bi: true },
  { id: 'e6',  from: 'batch-ingest',     to: 'conversion-queue',    pts: [[660, 154], [660, 248], [743, 248], [743, 492], [778, 492]], pill: [743, 400] },
  { id: 'e7',  from: 'encore-pg',        to: 'ingest-jobs',         pts: [[216, 332], [532, 332]],                                    pill: [374, 332] },
  { id: 'e8',  from: 'ingest-jobs',      to: 'store-tickets',       pts: [[708, 316], [778, 316]],                                    pill: [743, 316] },
  { id: 'e9',  from: 'ingest-jobs',      to: 'enrich',              pts: [[676, 300], [676, 228], [855, 228], [855, 216]],            pill: [740, 228], bi: true },
  { id: 'e10', from: 'store-tickets',    to: 'd1-stats',            pts: [[866, 340], [866, 368]],                                    pill: [878, 354], align: 'left', static: true },
  { id: 'e11', from: 'conversion-queue', to: 'meta',                pts: [[954, 482], [989, 482], [989, 456], [1024, 456]],           pill: [989, 470] },
  { id: 'e12', from: 'conversion-queue', to: 'ttd',                 pts: [[954, 502], [989, 502], [989, 528], [1024, 528]],           pill: [989, 516] },
  // Ticket conversions: the tickets store feeds the queue directly (EncorePro
  // sales are the conversions every destination receives today). Routed down
  // the clear column right of the store cards and into the queue's top, so it
  // crosses neither Daily Stats nor the destination wires.
  { id: 'e13', from: 'store-tickets',    to: 'conversion-queue',    pts: [[954, 330], [989, 330], [989, 446], [920, 446], [920, 460]], pill: [989, 392] },
];

/** Context-panel grouping per node (design §5.6). */
export const DP_NODE_GROUP: Record<DpNodeId, 'sources' | 'store' | 'encore' | 'activation'> = {
  'web-pixel': 'sources', 'sgtm': 'sources', 'pixel-edge': 'sources',
  'batch-ingest': 'store', 'store-web-events': 'store', 'enrich': 'store',
  'encore-pg': 'encore', 'ingest-jobs': 'encore', 'store-tickets': 'encore', 'd1-stats': 'encore',
  'conversion-queue': 'activation', 'meta': 'activation', 'ttd': 'activation',
};

// ── Path math ──────────────────────────────────────────────────────────────

/** Rounded orthogonal SVG path through the waypoints. */
export function dpElbow(pts: readonly DpPoint[], r = 10): string {
  const first = pts[0];
  const last = pts[pts.length - 1];
  if (!first || !last) return '';
  let d = 'M' + first[0] + ' ' + first[1];
  for (let i = 1; i < pts.length - 1; i++) {
    const p0 = pts[i - 1]!;
    const p1 = pts[i]!;
    const p2 = pts[i + 1]!;
    const v1 = [Math.sign(p1[0] - p0[0]), Math.sign(p1[1] - p0[1])] as const;
    const v2 = [Math.sign(p2[0] - p1[0]), Math.sign(p2[1] - p1[1])] as const;
    const l1 = Math.abs(p1[0] - p0[0]) + Math.abs(p1[1] - p0[1]);
    const l2 = Math.abs(p2[0] - p1[0]) + Math.abs(p2[1] - p1[1]);
    const rr = Math.min(r, l1 / 2, l2 / 2);
    d += ' L' + (p1[0] - v1[0] * rr) + ' ' + (p1[1] - v1[1] * rr);
    d += ' Q' + p1[0] + ' ' + p1[1] + ' ' + (p1[0] + v2[0] * rr) + ' ' + (p1[1] + v2[1] * rr);
  }
  d += ' L' + last[0] + ' ' + last[1];
  return d;
}

/** Arrowhead polygon points at segment end (direction from a → b). */
export function dpArrow(a: DpPoint, b: DpPoint): string {
  const dx = Math.sign(b[0] - a[0]);
  const dy = Math.sign(b[1] - a[1]);
  const s = 5.5;
  const x = b[0];
  const y = b[1];
  if (dx === 1) return `${x},${y} ${x - s},${y - s * 0.72} ${x - s},${y + s * 0.72}`;
  if (dx === -1) return `${x},${y} ${x + s},${y - s * 0.72} ${x + s},${y + s * 0.72}`;
  if (dy === 1) return `${x},${y} ${x - s * 0.72},${y - s} ${x + s * 0.72},${y - s}`;
  return `${x},${y} ${x - s * 0.72},${y + s} ${x + s * 0.72},${y + s}`;
}

// ── Live derivation (§5.2/5.3) ─────────────────────────────────────────────

export type DpHealth = 'up' | 'degraded' | 'down' | 'quiet';
export type DpSpeed = 'idle' | 'slow' | 'normal' | 'fast';

export interface DpNodeState {
  health: DpHealth;
  fresh: boolean;
  stat: string;
  tip: Record<string, string>;
}

export interface DpEdgeState {
  num: string;
  unit: string;
  speed: DpSpeed;
  /** Final CSS class: the speed, 'warn <speed>' (downstream degraded) or 'stopped' (downstream down). */
  cls: string;
  static?: boolean;
}

export interface DpDerived {
  nodes: Record<DpNodeId, DpNodeState>;
  edges: Record<DpEdgeId, DpEdgeState>;
}

/** March-speed bucket from a per-minute rate. */
export function dpSpeed(perMinRate: number): DpSpeed {
  return perMinRate <= 0 ? 'idle' : perMinRate < 10 ? 'slow' : perMinRate < 200 ? 'normal' : 'fast';
}

export function dpHealthColor(h: DpHealth | 'neutral'): string {
  return h === 'up' ? 'var(--good)'
    : h === 'degraded' ? 'var(--warn)'
    : h === 'down' ? 'var(--bad)'
    : h === 'neutral' ? 'var(--line-strong)'
    : 'var(--ink-3)';
}

interface TargetCounts {
  pending: number;
  enqueued_1h: number;
  /** Delivered — or, in log_only, LOGGED (built and logged, never sent). */
  delivered_1h: number;
  failed_1h: number;
  delivered_24h: number;
  failed_24h: number;
}

const ZERO_TARGET: TargetCounts = {
  pending: 0, enqueued_1h: 0, delivered_1h: 0, failed_1h: 0, delivered_24h: 0, failed_24h: 0,
};

/** The per-target counters every queue row type carries (by target, or by source and target). */
interface QueueCounters {
  pending: number;
  enqueued_1h: number;
  delivered_1h: number;
  failed_1h: number;
  delivered_24h: number;
  failed_24h: number;
  logged_1h: number;
  logged_24h: number;
}

/**
 * One target's counters, log-only aware: in `log_only` nothing is sent, so the
 * throughput is the LOGGED rows alone. Failures are never folded into it (they
 * used to be, as "logged/h"); they stay in `failed_*`, shown beside it.
 */
export function targetCounts(t: QueueCounters | undefined, logOnly: boolean): TargetCounts {
  return t
    ? {
        pending: num(t.pending), enqueued_1h: num(t.enqueued_1h),
        delivered_1h: num(logOnly ? t.logged_1h : t.delivered_1h), failed_1h: num(t.failed_1h),
        delivered_24h: num(logOnly ? t.logged_24h : t.delivered_24h), failed_24h: num(t.failed_24h),
      }
    : ZERO_TARGET;
}

const FALLBACK_COMP: Pick<ComponentHealth, 'status' | 'detail' | 'last_seen'> = {
  status: 'quiet', detail: '', last_seen: null,
};

/// `display_policy`, `metrics` and `encorepro.schedule` are newer than the
/// oldest API/batch-worker a browser can be talking to (Pages and the
/// Workers deploy independently). They are `#[serde(default)]` server-side, but
/// a frontend that ships first would otherwise hard-crash the whole screen on
/// `snapshot.metrics.metrics`. Degrade to "no server policy" instead: an absent
/// metric already means `quiet` in `metricHealth`.
const FALLBACK_POLICY = {
  source_fresh_secs: 120, source_quiet_secs: 600,
  snapshot_cache_ttl_secs: 60, run_heartbeat_stale_secs: 600,
} as unknown as PipelineFlowSnapshot['display_policy'];

/** What the Data Pipeline header chip says, and what it shows on hover. */
export interface DpHealthChip {
  band: 'ok' | 'warn' | 'critical';
  label: string;
  tip: string;
}

/// One store's word in the `data_stores` detail.
export type StoreWord = 'ok' | 'degraded' | 'down' | 'unknown';

/// Per-store words read back out of the `data_stores` detail.
///
/// The component's status is the worst of PostgreSQL, Trino and the lake, so
/// the diagram needs each store's own word. The API's `data_stores_health`
/// writes them as `postgres <word>[ (migration N…)] · trino <word> · lake
/// <word>[ (sealed through D)]`, with a healthy lake spelled `lake sealed
/// through D`; `the_data_stores_detail_carries_one_word_per_store` pins that
/// on the Rust side. Anything else — "no store report", "heartbeat stale" —
/// yields no words, which leaves every node as traffic says.
export function dataStoreWords(detail: string | null | undefined): {
  postgres?: StoreWord;
  trino?: StoreWord;
  lake?: StoreWord;
} {
  const out: { postgres?: StoreWord; trino?: StoreWord; lake?: StoreWord } = {};
  for (const part of (detail ?? '').split(' \u00b7 ')) {
    const m = /^(postgres|trino|lake) (ok|degraded|down|unknown)(?: |$)/.exec(part);
    if (m) out[m[1] as 'postgres' | 'trino' | 'lake'] = m[2] as StoreWord;
    else if (/^lake sealed through /.test(part)) out.lake = 'ok';
  }
  return out;
}

/// Summarize `health.components` for the page header.
///
/// The chip covers every component; the diagram below it renders nodes for
/// only four (`batch_worker`, `ingest`, `pixel_worker`, and `data_stores`, whose
/// per-store words reach the two PostgreSQL store nodes — see `dpDerive`).
/// A degraded `api_worker`, `frontend` or `pixel_flow` therefore produced
/// "1 degraded" above a diagram in which every node was green, with nothing on
/// the page to say which component or why.
///
/// Naming the component fixes the contradiction instead of hiding it: scoping
/// the chip to the three rendered components would make a real API 5xx
/// invisible on this screen.
export function dpHealthChip(
  components: readonly ComponentHealth[],
  logOnly: boolean,
): DpHealthChip {
  const down = components.filter((c) => c.status === 'down');
  const degraded = components.filter((c) => c.status === 'degraded');
  const names = (cs: readonly ComponentHealth[]) =>
    cs.map((c) => componentLabel(c.component)).join(', ');
  const band = down.length ? 'critical' : degraded.length || logOnly ? 'warn' : 'ok';
  const label = down.length
    ? `${names(down)} down`
    : logOnly
      ? 'Log-only delivery'
      : degraded.length
        ? `${names(degraded)} degraded`
        : 'Pipeline healthy';
  const tip = components.length
    ? components
        .map((c) =>
          `${componentLabel(c.component)} — ${c.status.toUpperCase()}${c.detail ? ` · ${componentDetail(c.component, c.detail)}` : ''}`,
        )
        .join('\n')
    : 'no component health reported';
  return { band, label, tip };
}

/** Derive node health + stats + tips and edge speeds + labels from a snapshot. */
export function dpDerive(snapshot: PipelineFlowSnapshot, nowMs: number): DpDerived {
  const b = snapshot.batch;
  const policy = snapshot.display_policy ?? FALLBACK_POLICY;
  const compMap = new Map(snapshot.health.components.map((c) => [c.component, c]));
  const metricMap = new Map((snapshot.metrics?.metrics ?? []).map((m) => [m.key, m]));
  const comp = (id: string): Pick<ComponentHealth, 'status' | 'detail' | 'last_seen'> =>
    compMap.get(id) ?? FALLBACK_COMP;

  const rows = (src: IngestChannel): FlowRateRow[] => b.web_events.filter((r) => r.source === src);
  const rate5 = (src: IngestChannel) => rows(src).reduce((s, r) => s + num(r.count_5m), 0) / 5;
  const sum24 = (rws: FlowRateRow[]) => rws.reduce((s, r) => s + num(r.count_24h), 0);
  const newest = (...isos: (string | null)[]): string | null =>
    isos.filter((t): t is string => !!t).sort().pop() ?? null;
  const lastAt = (src: IngestChannel): string | null => newest(...rows(src).map((r) => r.last_at));

  const perMin = { pixel: rate5('pixel'), sgtm: rate5('pixel_sgtm') };
  const allMin = perMin.pixel + perMin.sgtm;
  const web24 = sum24(rows('pixel')) + sum24(rows('pixel_sgtm'));
  /** The newest web event of EITHER pixel: the browser pixel and the server one both feed the store. */
  const webLast = newest(lastAt('pixel'), lastAt('pixel_sgtm'));
  const conv = b.conversions;
  // In log_only nothing is sent: the destination's throughput is its logged
  // rows, labelled "logged" below. Delivered never includes them.
  const logOnlyMode = conv.mode === 'log_only';
  const outcome = logOnlyMode ? 'logged' : 'delivered';
  const meta = targetCounts(conv.by_target.find((x) => x.target === 'meta'), logOnlyMode);
  const ttd = targetCounts(conv.by_target.find((x) => x.target === 'ttd'), logOnlyMode);
  // Enqueues per SOURCE: web conversions leave Batch Ingest, ticket conversions
  // leave the tickets store. Older servers send no breakdown: the web edge then
  // falls back to every enqueue, as it always drew.
  const bySource = conv.by_source_target ?? [];
  const enqueuedFrom = (source: string) =>
    bySource.filter((x) => x.source === source).reduce((s, x) => s + num(x.enqueued_1h), 0);
  const webEnq = bySource.length ? enqueuedFrom('web_event') : meta.enqueued_1h + ttd.enqueued_1h;
  const ticketEnq = enqueuedFrom('ticket');
  const oldestPendingSecs = conv.oldest_pending_age_secs == null ? null : num(conv.oldest_pending_age_secs);

  // The last TERMINAL run in scope (completed, failed, interrupted…) and, apart
  // from it, a run still in flight. The snapshot used to carry only "the newest
  // run of any kind", so a queued or manual single-production run could stand
  // in for the scheduled one.
  const run = snapshot.last_ingest_run;
  const live = snapshot.running_ingest_run ?? null;
  const runFinishedIso = run?.finished_at != null ? new Date(num(run.finished_at)).toISOString() : null;
  const runAge = runFinishedIso ? dpAge(runFinishedIso, nowMs) : '—';
  const runLag = run?.source_lag_hours == null ? null : num(run.source_lag_hours);
  const rz = b.resolve_1h;
  // Person match is measured on PURCHASES, live and history alike: a pageview
  // carries no identity to match, so dividing by every event read low.
  const purchases1h = num(rz.purchases_1h);
  const purchaseMatched1h = num(rz.purchase_person_1h);
  const matchPct = ratePct(purchaseMatched1h, purchases1h);
  const matchLabel = matchPct == null ? '—' : Math.round(matchPct) + '%';

  // Source activity: no event in 10 minutes ⇒ 'quiet' (a coverage gap, not an outage).
  const activity = (at: string | null) => {
    const secs = dpAgeSecs(at, nowMs);
    if (!at || secs > num(policy.source_quiet_secs)) {
      return {
        health: 'quiet' as DpHealth,
        stat: at ? 'no events ' + dpAge(at, nowMs).replace(' ago', '') : 'no signal',
        fresh: false,
        last: at,
      };
    }
    return { health: 'up' as DpHealth, stat: null, fresh: secs < num(policy.source_fresh_secs), last: at };
  };
  const compHealth = (id: string): DpHealth => {
    const s = comp(id).status;
    return s === 'up' || s === 'degraded' || s === 'down' ? s : 'quiet';
  };
  const metricHealth = (key: string, value: number | null): DpHealth => {
    const metric = metricMap.get(key);
    const observed = value ?? metric?.value ?? null;
    if (!metric || metric.stale || observed == null) return 'quiet';
    const band = bandFor(metric, observed, 'unknown');
    return band === 'critical' ? 'down' : band === 'warn' ? 'degraded' : band === 'ok' ? 'up' : 'quiet';
  };
  const destHealth = (target: 'meta' | 'ttd', t: TargetCounts): DpHealth => {
    const enabled = target === 'meta' ? conv.meta_enabled : conv.ttd_enabled;
    const circuitOpen = target === 'meta' ? conv.meta_circuit_open : conv.ttd_circuit_open;
    if (!enabled) return 'quiet';
    if (circuitOpen) return 'down';
    return t.delivered_1h + t.failed_1h > 0 ? 'up' : 'quiet';
  };
  /** Errors can only make a traffic-derived health worse. */
  const worsen = (h: DpHealth, by: DpHealth): DpHealth =>
    by === 'down' ? 'down' : by === 'degraded' && h !== 'down' ? 'degraded' : h;

  const wp = activity(lastAt('pixel'));
  const sg = activity(lastAt('pixel_sgtm'));
  const web = activity(webLast);
  const queueHealth = metricHealth('capi.oldest_pending_secs', oldestPendingSecs);
  const pixelWorker = comp('pixel_worker');
  const batchWorker = comp('batch_worker');
  // `data_stores` is batch's own probe of PostgreSQL, Trino and the lake,
  // carried in its heartbeat. Its status is the WORST of the three, so it is
  // never applied to a node whole: a Trino probe failing or the lake sealing
  // late says nothing about PostgreSQL. Each node takes only the stores it
  // draws — both take `postgres`, `store-web-events` also takes `lake` (its
  // kind says "+ LAKE"), and Trino stays on the header chip. A store word can
  // only make a node worse: one the prober saw down is down whatever the
  // traffic says, and an absent or unparsed report (an older batch build, a
  // stale beat) leaves the node as it was.
  const dataStores = comp('data_stores');
  const storeWords = dataStoreWords(dataStores.detail);
  const withStores = (h: DpHealth, ...stores: (StoreWord | undefined)[]): DpHealth =>
    stores.includes('down') ? 'down'
      : stores.includes('degraded') && h !== 'down' ? 'degraded'
      : h;
  const storesTip = formatStoreDetail(dataStores.detail) || dataStores.status;
  // A failed or reaper-interrupted last run means the window was NOT ingested.
  const runBroken = run != null && (run.status === 'failed' || run.status === 'interrupted');
  const runningStatus = live != null && live.status === 'running';
  const heartbeatFresh = runningStatus && live.heartbeat_at != null
    && nowMs - num(live.heartbeat_at) <= num(policy.run_heartbeat_stale_secs) * 1000;
  const staleRunning = runningStatus && !heartbeatFresh;
  /// A recent heartbeat is direct evidence EncorePro is being read now.
  const runningNow = heartbeatFresh;
  const liveStat = live
    ? (live.status === 'running' ? 'running · ' + (live.progress ?? 0) + '%' : live.status)
    : null;

  // Pixel Collector: nothing reports its liveness, so its node follows the web
  // traffic it relays. No events in 24h is Quiet (no pixel may be installed
  // yet — the page never claims either way); the component's error lines can
  // still make it degraded or down.
  const pixelTraffic: DpHealth = web24 > 0 ? 'up' : 'quiet';
  const pixelHealth = worsen(pixelTraffic, compHealth('pixel_worker'));

  // Daily stats: the per-production rollup's own freshness.
  const stats = b.production_stats;
  const statsRows = num(stats?.rows);
  const statsAt = stats?.last_snapshot_at ?? null;

  const nodes: Record<DpNodeId, DpNodeState> = {
    'web-pixel': {
      health: wp.health, fresh: wp.fresh,
      stat: wp.health === 'up' ? dpN(perMin.pixel) + '/min' : wp.stat!,
      tip: { 'last tick': dpN(perMin.pixel) + '/min', 'last event': dpAge(wp.last, nowMs), '24h': dpN(sum24(rows('pixel'))) },
    },
    'sgtm': {
      health: sg.health, fresh: sg.fresh,
      stat: sg.health === 'up' ? dpN(perMin.sgtm) + '/min' : sg.stat!,
      tip: { 'last tick': dpN(perMin.sgtm) + '/min', 'last event': dpAge(sg.last, nowMs), '24h': dpN(sum24(rows('pixel_sgtm'))) },
    },
    'pixel-edge': {
      health: pixelHealth,
      fresh: pixelHealth === 'up' && allMin > 0,
      stat: pixelHealth === 'down' || pixelHealth === 'degraded'
        ? (pixelWorker.detail || pixelWorker.status)
        : web24 === 0
          ? 'no events in 24h'
          : 'last event ' + dpAge(webLast, nowMs),
      tip: {
        'last event': dpAge(webLast, nowMs),
        'events 24h': dpN(web24),
        errors: pixelWorker.status === 'degraded' || pixelWorker.status === 'down'
          ? (pixelWorker.detail || pixelWorker.status)
          : 'none reported',
      },
    },
    'batch-ingest': {
      health: compHealth('batch_worker'), fresh: compHealth('batch_worker') === 'up' && allMin > 0,
      stat: allMin > 0
        ? dpN(allMin) + '/min ingest'
        : (webLast ? 'no input ' + dpAge(webLast, nowMs).replace(' ago', '') : 'no input'),
      tip: { status: batchWorker.status, ingest: dpN(allMin) + '/min', 'person match 1h': matchLabel },
    },
    'store-web-events': {
      health: withStores(web.health === 'up' ? 'up' : 'quiet', storeWords.postgres, storeWords.lake), fresh: web.fresh,
      stat: webLast ? 'newest ' + dpAge(webLast, nowMs).replace(' ago', ' old') : 'no rows in scope',
      tip: {
        store: 'last 8 days here, older days in the Data Lake',
        newest: dpAge(webLast, nowMs),
        'rows 24h': dpN(web24),
        stores: storesTip,
      },
    },
    'enrich': {
      health: wp.health === 'up' || sg.health === 'up' ? 'up' : 'quiet',
      fresh: purchases1h > 0 && (wp.fresh || sg.fresh),
      stat: matchLabel + ' match 1h',
      tip: { 'purchases 1h': dpN(purchases1h), matched: dpN(purchaseMatched1h), 'person match · purchases': matchLabel },
    },
    'encore-pg': {
      // EncorePro is a scheduled batch source, so use the server-delivered
      // `encore.source_lag_hours` thresholds rather than browser cut-points.
      //
      // A run IN FLIGHT is direct evidence the source is reachable and being
      // read right now, so it caps the band at 'degraded': the lag is real and
      // still worth showing, but 'down' would contradict the read in progress —
      // and 'down' on either endpoint forces the edge to 'stopped', which is why
      // EncorePro → Ingest Jobs sat dead mid-backfill.
      //
      // `fresh` follows the same signal. It was hardcoded false, so this node
      // could never render as live no matter what the source was doing.
      health: staleRunning
        ? 'degraded'
        : runningNow
          ? (metricHealth('encore.source_lag_hours', runLag) === 'down' ? 'degraded' : 'up')
          : metricHealth('encore.source_lag_hours', runLag),
      fresh: runningNow,
      stat: 'lag ' + (runLag != null ? runLag : '—') + 'h',
      tip: { 'source lag': (runLag != null ? runLag : '—') + 'h', 'last read': runAge },
    },
    'ingest-jobs': {
      health: runBroken ? 'down' : staleRunning ? 'degraded' : compHealth('ingest'),
      fresh: runningNow,
      stat: liveStat ?? (run ? run.trigger + ' · ' + run.status : 'no runs'),
      tip: {
        'last run': run ? run.trigger + ' · ' + run.status : '—',
        finished: runAge,
        'in flight': live ? live.trigger + ' · ' + liveStat : 'none',
        schedule: b.encorepro.schedule?.cron || 'unavailable',
        timezone: b.encorepro.schedule?.timezone || 'unavailable',
      },
    },
    'store-tickets': {
      health: withStores(
        runBroken ? 'down' : staleRunning ? 'degraded' : run != null && (run.status === 'completed' || runningNow) ? 'up' : 'quiet',
        storeWords.postgres,
      ),
      fresh: runningNow,
      stat: run && run.tickets_out != null
        ? dpN(num(run.tickets_out)) + ' tickets · ' + runAge.replace(' ago', '')
        : '—',
      tip: {
        store: 'tickets / users',
        'last load': runAge,
        'tickets last run': run && run.tickets_out != null ? dpN(num(run.tickets_out)) : '—',
        stores: storesTip,
      },
    },
    'd1-stats': {
      health: statsAt != null && statsRows > 0 ? 'up' : 'quiet',
      fresh: false,
      stat: statsAt != null
        ? 'last rollup ' + dpAge(statsAt, nowMs) + ' · ' + dpN(statsRows) + ' rows'
        : 'no rollup yet',
      tip: {
        'last rollup': dpAge(statsAt, nowMs),
        rows: dpN(statsRows),
        days: 'UTC days (one row per production per day)',
      },
    },
    'conversion-queue': {
      health: queueHealth,
      fresh: queueHealth === 'up' && meta.enqueued_1h + ttd.enqueued_1h > 0,
      stat: 'depth ' + dpN(num(conv.queue_depth)) + ' · oldest ' + dpDur(oldestPendingSecs),
      tip: {
        depth: dpN(num(conv.queue_depth)),
        oldest: dpDur(oldestPendingSecs),
        'enqueued 1h': bySource.length
          ? 'web ' + dpN(webEnq) + ' · tickets ' + dpN(ticketEnq)
          : dpN(meta.enqueued_1h + ttd.enqueued_1h),
        mode: conv.mode === 'live' ? 'LIVE' : conv.mode === 'log_only' ? 'LOG-ONLY' : 'OFF',
      },
    },
    'meta': {
      health: destHealth('meta', meta), fresh: destHealth('meta', meta) === 'up' && meta.delivered_1h > 0,
      stat: dpN(meta.delivered_1h) + (logOnlyMode ? ' logged/h · ' : '/h · ') + meta.failed_1h + ' failed',
      tip: {
        configured: conv.meta_enabled ? 'yes' : 'no',
        circuit: conv.meta_circuit_open ? 'OPEN' : 'closed',
        [`${outcome} 1h`]: dpN(meta.delivered_1h),
        'failed 1h': dpN(meta.failed_1h),
        [`${outcome} 24h`]: dpN(meta.delivered_24h),
        'failed 24h': dpN(meta.failed_24h),
      },
    },
    'ttd': {
      health: destHealth('ttd', ttd), fresh: destHealth('ttd', ttd) === 'up' && ttd.delivered_1h > 0,
      stat: dpN(ttd.delivered_1h) + (logOnlyMode ? ' logged/h · ' : '/h · ') + ttd.failed_1h + ' failed',
      tip: {
        configured: conv.ttd_enabled ? 'yes' : 'no',
        circuit: conv.ttd_circuit_open ? 'OPEN' : 'closed',
        [`${outcome} 1h`]: dpN(ttd.delivered_1h),
        'failed 1h': dpN(ttd.failed_1h),
        [`${outcome} 24h`]: dpN(ttd.delivered_24h),
        'failed 24h': dpN(ttd.failed_24h),
      },
    },
  };

  const runRate: DpSpeed = runningNow ? 'slow' : 'idle';
  const runN = (v: number | null | undefined) => (run != null && v != null ? dpN(num(v)) : '—');
  const destEdge = (t: TargetCounts): DpEdgeState => ({
    // log_only: logged rows only — failures are the node's own line, not throughput.
    num: dpN(t.delivered_1h),
    unit: logOnlyMode ? ' logged/h' : '/h',
    speed: dpSpeed(t.delivered_1h / 60), cls: '',
  });

  const edges: Record<DpEdgeId, DpEdgeState> = {
    e1:  { num: dpN(perMin.pixel), unit: '/min', speed: dpSpeed(perMin.pixel), cls: '' },
    e2:  { num: dpN(perMin.sgtm), unit: '/min', speed: dpSpeed(perMin.sgtm), cls: '' },
    e3:  { num: dpN(allMin), unit: '/min', speed: dpSpeed(allMin), cls: '' },
    e4:  { num: dpN(allMin), unit: '/min', speed: dpSpeed(allMin), cls: '' },
    e5:  { num: matchLabel, unit: ' match 1h', speed: dpSpeed(purchases1h / 60), cls: '' },
    e6:  { num: dpN(webEnq), unit: ' enq/h', speed: dpSpeed(webEnq / 60), cls: '' },
    e7:  { num: runN(run?.tickets_in), unit: '/run · scheduled', speed: runRate, cls: '' },
    e8:  { num: runN(run?.tickets_out), unit: '/run', speed: runRate, cls: '' },
    e9:  { num: runN(run?.resolved_ct), unit: ' sent/run', speed: runRate, cls: '' },
    e10: { num: 'CRON', unit: '', speed: 'idle', cls: '', static: true },
    e11: destEdge(meta),
    e12: destEdge(ttd),
    e13: { num: dpN(ticketEnq), unit: ' enq/h', speed: dpSpeed(ticketEnq / 60), cls: '' },
  };

  // An outage is SEEN on the wire: a down node stops every edge touching it
  // (nothing flows in OR out); a degraded downstream tints the flow.
  for (const e of DP_EDGES) {
    const st = edges[e.id];
    const downstream = nodes[e.to].health;
    if (downstream === 'down' || nodes[e.from].health === 'down') st.cls = 'stopped';
    else if (downstream === 'degraded') st.cls = 'warn ' + st.speed;
    else st.cls = st.speed;
  }
  return { nodes, edges };
}

/**
 * A batch-worker cron (`sec min hour dom month dow`, tokio-cron-scheduler) in
 * words, for the shapes the schedules use: daily at one or more hours, or every
 * N hours. Anything else is shown as written rather than guessed at.
 */
export function cronWords(cron: string | null | undefined, timezone: string | null | undefined): string {
  if (!cron) return 'Schedule unavailable';
  const zone = timezone === 'America/New_York' ? 'ET' : timezone || 'UTC';
  const fields = cron.trim().split(/\s+/);
  if (fields.length !== 6) return `Schedule ${cron} (${zone})`;
  const [sec, min, hour, dom, month, dow] = fields as [string, string, string, string, string, string];
  const everyDay = dom === '*' && month === '*' && dow === '*';
  const minute = Number(min);
  if (!everyDay || sec !== '0' || !/^\d+$/.test(min) || minute > 59) return `Schedule ${cron} (${zone})`;
  const step = /^\*\/(\d+)$/.exec(hour);
  if (step) return `Every ${step[1]} hours at :${String(minute).padStart(2, '0')} ${zone}`;
  const hours = hour.split(',');
  if (hours.every((h) => /^\d+$/.test(h) && Number(h) < 24)) {
    const at = hours.map((h) => `${String(Number(h)).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
    return `Daily at ${at.join(', ')} ${zone}`;
  }
  return `Schedule ${cron} (${zone})`;
}
